import type { User } from '@/types/auth';
import type { SalespersonStats } from '@/types/session';
import type { VendorProfile } from '@/types/vendor-kpi';
import { getCardTier } from '@/types/vendor-kpi';
import { buildVendorProfileFromAverages } from './vendorProfileBuilder';

export type TierFilter = 'all' | 'elite' | 'pro' | 'standard' | 'unrated';
export type ActivityFilter = 'all' | 'with' | 'without';
export type TeamFilter = 'all' | 'unassigned' | string;

export type CardsSortMode = 'alphabetical' | 'sessions' | 'createdAt';
export type TableSortMode = 'alphabetical' | 'createdAt' | 'email' | 'sessions';

export type TeamFilters = {
  search: string;
  tier: TierFilter;
  activity: ActivityFilter;
  team: TeamFilter;
  sortCards: CardsSortMode;
  sortTable: TableSortMode;
};

export const DEFAULT_TEAM_FILTERS: TeamFilters = {
  search: '',
  tier: 'all',
  activity: 'all',
  team: 'all',
  sortCards: 'alphabetical',
  sortTable: 'alphabetical',
};

export function isFiltersActive(f: TeamFilters): boolean {
  return (
    f.search.trim() !== '' ||
    f.tier !== 'all' ||
    f.activity !== 'all' ||
    f.team !== 'all'
  );
}

export type EnrichedMember = {
  user: User;
  stats?: SalespersonStats;
  profile: VendorProfile | null;
  globalScore: number;
};

export function enrichMembers(
  members: User[],
  statsByUserId: Record<string, SalespersonStats>,
): EnrichedMember[] {
  return members.map((m) => {
    const stats = statsByUserId[m.id];
    const profile = stats?.kpiAveragesJson
      ? buildVendorProfileFromAverages(stats.kpiAveragesJson)
      : null;
    return { user: m, stats, profile, globalScore: profile?.globalScore ?? 0 };
  });
}

function memberTier(m: EnrichedMember): Exclude<TierFilter, 'all'> {
  if (m.globalScore === 0) return 'unrated';
  return getCardTier(m.globalScore);
}

export function applyFilters(list: EnrichedMember[], f: TeamFilters): EnrichedMember[] {
  const q = f.search.trim().toLowerCase();
  return list.filter((m) => {
    if (q) {
      const haystack = `${m.user.firstName} ${m.user.lastName} ${m.user.email}`.toLowerCase();
      if (!haystack.includes(q)) return false;
    }
    if (f.tier !== 'all' && memberTier(m) !== f.tier) return false;
    const hasSessions = (m.stats?.sessionCount ?? 0) > 0;
    if (f.activity === 'with' && !hasSessions) return false;
    if (f.activity === 'without' && hasSessions) return false;
    return true;
  });
}

function compareAlpha(a: EnrichedMember, b: EnrichedMember): number {
  const la = (a.user.lastName ?? '').localeCompare(b.user.lastName ?? '', undefined, { sensitivity: 'base' });
  if (la !== 0) return la;
  return (a.user.firstName ?? '').localeCompare(b.user.firstName ?? '', undefined, { sensitivity: 'base' });
}

export function sortCardsMembers(list: EnrichedMember[], mode: CardsSortMode): EnrichedMember[] {
  const copy = [...list];
  switch (mode) {
    case 'alphabetical':
      copy.sort(compareAlpha);
      break;
    case 'sessions':
      copy.sort((a, b) => (b.stats?.sessionCount ?? 0) - (a.stats?.sessionCount ?? 0) || compareAlpha(a, b));
      break;
    case 'createdAt':
      copy.sort((a, b) => (b.user.createdAt ?? '').localeCompare(a.user.createdAt ?? '') || compareAlpha(a, b));
      break;
  }
  return copy;
}

export function sortTableMembers(list: EnrichedMember[], mode: TableSortMode): EnrichedMember[] {
  const copy = [...list];
  switch (mode) {
    case 'alphabetical':
      copy.sort(compareAlpha);
      break;
    case 'createdAt':
      copy.sort((a, b) => (b.user.createdAt ?? '').localeCompare(a.user.createdAt ?? '') || compareAlpha(a, b));
      break;
    case 'email':
      copy.sort((a, b) => (a.user.email ?? '').localeCompare(b.user.email ?? '') || compareAlpha(a, b));
      break;
    case 'sessions':
      copy.sort((a, b) => (b.stats?.sessionCount ?? 0) - (a.stats?.sessionCount ?? 0) || compareAlpha(a, b));
      break;
  }
  return copy;
}
